/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ApiResponse } from '../../types/apiTypes';
import {
  Agreement,
  AgreementContent,
  CreateAgreementPayload,
  UpdateAgreementPayload,
  UpdateAgreementContentPayload,
} from '../../types/agreementTypes';

export interface AgreementsState {
  agreements: ApiResponse<Agreement>;
  selectedAgreement: Agreement | null;
  selectedDocumentContent: AgreementContent | null;
  fetchAgreementsLoading: boolean;
  describeAgreementLoading: boolean;
  createAgreementLoading: boolean;
  updateAgreementLoading: boolean;
  deleteAgreementLoading: boolean;
  describeAgreementContentLoading: boolean;
  updateAgreementContentLoading: boolean;
  error: string | null;
}

const initialState: AgreementsState = {
  agreements: { data: [], meta: null, totalCount: 0, filteredCount: 0 },
  selectedAgreement: null,
  selectedDocumentContent: null,
  fetchAgreementsLoading: false,
  describeAgreementLoading: false,
  createAgreementLoading: false,
  updateAgreementLoading: false,
  deleteAgreementLoading: false,
  describeAgreementContentLoading: false,
  updateAgreementContentLoading: false,
  error: null,
};

type AgreementsLoadingKey =
  | 'fetchAgreementsLoading'
  | 'describeAgreementLoading'
  | 'createAgreementLoading'
  | 'updateAgreementLoading'
  | 'deleteAgreementLoading'
  | 'describeAgreementContentLoading'
  | 'updateAgreementContentLoading';

const agreementsSlice = createSlice({
  name: 'agreements',
  initialState,
  reducers: {
    fetchAgreements(
      state,
      _action: PayloadAction<{ orgId: number; status?: number }>,
    ) {
      state.error = null;
    },
    describeAgreement(
      state,
      _action: PayloadAction<{ orgId: number; agreementId: number }>,
    ) {
      state.error = null;
    },
    createAgreement(
      _state,
      _action: PayloadAction<{
        orgId: number;
        agreement: Partial<CreateAgreementPayload>;
        content?: string;
      }>,
    ) {},
    updateAgreement(
      _state,
      _action: PayloadAction<{
        orgId: number;
        agreementId: number;
        agreement: Partial<UpdateAgreementPayload>;
      }>,
    ) {},
    deleteAgreement(
      _state,
      _action: PayloadAction<{ orgId: number; agreementId: number }>,
    ) {},
    describeAgreementContent(
      _state,
      _action: PayloadAction<{ orgId: number; agreementId: number }>,
    ) {},
    updateAgreementContent(
      _state,
      _action: PayloadAction<{
        orgId: number;
        agreementId: number;
        content: Partial<UpdateAgreementContentPayload>;
      }>,
    ) {},

    setAgreements(state, action: PayloadAction<ApiResponse<Agreement>>) {
      state.agreements = action.payload;
    },
    setSelectedAgreement(state, action: PayloadAction<Agreement | null>) {
      state.selectedAgreement = action.payload;
    },
    setSelectedDocumentContent(
      state,
      action: PayloadAction<AgreementContent | null>,
    ) {
      state.selectedDocumentContent = action.payload;
    },
    setLoading(
      state,
      action: PayloadAction<{ key: AgreementsLoadingKey; value: boolean }>,
    ) {
      state[action.payload.key] = action.payload.value;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },

    clearAgreements(state) {
      state.agreements = initialState.agreements;
    },
    clearSelectedAgreement(state) {
      state.selectedAgreement = initialState.selectedAgreement;
      state.selectedDocumentContent = initialState.selectedDocumentContent;
    },
    clearAgreementsState() {
      return initialState;
    },
  },
});

export const {
  fetchAgreements,
  describeAgreement,
  createAgreement,
  updateAgreement,
  deleteAgreement,
  describeAgreementContent,
  updateAgreementContent,
  setAgreements,
  setSelectedAgreement,
  setSelectedDocumentContent,
  setLoading,
  setError,
  clearAgreements,
  clearSelectedAgreement,
  clearAgreementsState,
} = agreementsSlice.actions;

export const agreementsActions = agreementsSlice.actions;
export default agreementsSlice.reducer;
